import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminSidebar from './AdminSidebar';
import CalendarView from './CalendarView';
import BookingManagement from './BookingManagement';
import BulkBooking from './BulkBooking';
import SlotBlocking from './SlotBlocking';
import Reports from './Reports';
import Finance from './Finance';
import Settings from './Settings';
import NotificationManager from './NotificationManager';
import CommunityGames from './CommunityGames';
import EventsManagement from './EventsManagement';
import CoachingManagement from './CoachingManagement';
import TournamentManagement from './TournamentManagement';
import InventoryManagement from './InventoryManagement';
import OperationsManagement from './OperationsManagement';
import VEAttendance from './VEAttendance';
import StaffAttendance from './StaffAttendance';

// Protected route component for non-venue-manager routes
const ProtectedRoute = ({ children, isVenueManager }) => {
  if (isVenueManager) {
    return <Navigate to="/admin/calendar" replace />;
  }
  return children;
};

const AdminDashboard = () => {
  const { isAuthenticated, isVenueManager, isVenueOwner, isVenueFinance, isReadOnlyRole, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  // Wait for auth to finish loading before deciding
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-100">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // SEC-005: hard client-side section guard for read-only venue roles. Any path
  // outside the role's allowlist (even typed directly) redirects to the calendar.
  // The server independently 403s these sections regardless of the UI.
  if (isReadOnlyRole) {
    const allowed = isVenueOwner
      ? ['/admin/calendar', '/admin/reports']
      : ['/admin/calendar', '/admin/bookings', '/admin/finance'];
    const path = (location.pathname || '/admin').replace(/\/+$/, '') || '/admin';
    // '/admin' (index) is allowed exactly; everything else must match an allowed section.
    const ok = path === '/admin' || allowed.some((a) => path === a || path.startsWith(a + '/'));
    if (!ok) {
      return <Navigate to="/admin/calendar" replace />;
    }
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Notification prompts and banner */}
      <NotificationManager />
      
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      {/* Main content area - adjusted for mobile bottom nav */}
      <div className="flex-1 overflow-auto lg:ml-64">
        <Routes>
          <Route path="/" element={<CalendarView />} />
          <Route path="/calendar" element={<CalendarView />} />
          <Route path="/bookings" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <BookingManagement />
            </ProtectedRoute>
          } />
          <Route path="/bulk-booking" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <BulkBooking />
            </ProtectedRoute>
          } />
          <Route path="/community-games" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <CommunityGames />
            </ProtectedRoute>
          } />
          <Route path="/events/*" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <EventsManagement />
            </ProtectedRoute>
          } />
          <Route path="/coaching/*" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <CoachingManagement />
            </ProtectedRoute>
          } />
          <Route path="/tournaments/*" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <TournamentManagement />
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={<InventoryManagement />} />
          <Route path="/operations" element={<OperationsManagement />} />
          <Route path="/attendance" element={<VEAttendance />} />
          <Route path="/staff-attendance" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <StaffAttendance />
            </ProtectedRoute>
          } />
          <Route path="/blocks" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <SlotBlocking />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <Reports />
            </ProtectedRoute>
          } />
          <Route path="/finance" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <Finance />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute isVenueManager={isVenueManager}>
              <Settings />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </div>
  );
};

export default AdminDashboard;